'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useRef } from 'react';
import { InstructorDetails } from '@udemy-v2/browse-course';
import styles from './instructor-display.module.scss';
import classNames from 'classnames';
import { useI18n, LocalizedHtml } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { generateInstructorAnchorHash } from '../instructor-module/utils/instructor-utils';
import { Icon } from '@udemy-v2/icons';
import { publishEvent, TrackImpression } from '@udemy-v2/event-tracking';
import { useInstructorCommunityBannerInfo } from '@/lib/hooks/use-instructor-community-banner-info';
import { useInstructorDetails } from '@/lib/hooks/use-instructor-details';
import { CommunityPoweredBadge } from '../instructor-community/community-powered-badge';
import { CommunityPoweredRibbon } from '../instructor-community/community-powered-ribbon';
import { InstructorCommunityPromotionPresented, InstructorCommunityPromotionSelected, InstructorCommunityUiRegion } from '@/features/course-landing-page/events/events';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
interface InstructorDisplayProps {
  instructors: InstructorDetails[];
  isAGwGCourse?: boolean;
}

/**
 * Displays course instructors with links to their profiles
 *
 * @example
 * ```jsx
 * <InstructorDisplay instructors={course.instructors} />
 * ```
 */
export function InstructorDisplay(t0) {
  const $ = _c(38);
  const {
    instructors,
    isAGwGCourse: t1
  } = t0;
  const isAGwGCourse = t1 === undefined ? false : t1;
  const {
    gettext,
    interpolate
  } = useI18n();
  const firstInstructor = instructors?.[0];
  const {
    hasCommunity,
    communityId
  } = useInstructorCommunityBannerInfo(firstInstructor?.id);
  const instructorDetails = useInstructorDetails();
  const course = useClpCourse();
  const hasTrackedImpression = useRef(false);
  let t2;
  if ($[0] !== communityId || $[1] !== course.id || $[2] !== firstInstructor) {
    t2 = firstInstructor && communityId !== undefined ? {
      instructorId: Number(firstInstructor.id),
      communityId,
      courseId: Number(course.id),
      uiRegion: InstructorCommunityUiRegion.BADGE
    } : undefined;
    $[0] = communityId;
    $[1] = course.id;
    $[2] = firstInstructor;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const badgeEventArgs = t2;
  let t3;
  if ($[4] !== badgeEventArgs) {
    t3 = () => {
      if (!badgeEventArgs || hasTrackedImpression.current) {
        return;
      }
      hasTrackedImpression.current = true;
      publishEvent(new InstructorCommunityPromotionPresented(badgeEventArgs));
    };
    $[4] = badgeEventArgs;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleBadgeImpression = t3;
  let t4;
  if ($[6] !== badgeEventArgs) {
    t4 = () => {
      if (!badgeEventArgs) {
        return;
      }
      publishEvent(new InstructorCommunityPromotionSelected(badgeEventArgs));
    };
    $[6] = badgeEventArgs;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleBadgeSelected = t4;
  if (!instructors?.length) {
    return null;
  }
  const showCommunityPoweredBadge = !isAGwGCourse && hasCommunity;
  let t5;
  if ($[8] !== firstInstructor?.id || $[9] !== instructorDetails) {
    let t6;
    if ($[11] !== firstInstructor?.id) {
      t6 = instructor => instructor.id === firstInstructor?.id;
      $[11] = firstInstructor?.id;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = instructorDetails.find(t6);
    $[8] = firstInstructor?.id;
    $[9] = instructorDetails;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const firstInstructorDetails = t5;
  const firstInstructorImageUrl = firstInstructorDetails?.images.large ?? firstInstructor?.images.small;
  const handleOnInstructorNameClick = _temp;
  let t6;
  if ($[13] !== firstInstructor || $[14] !== firstInstructorImageUrl || $[15] !== handleBadgeImpression || $[16] !== handleBadgeSelected || $[17] !== showCommunityPoweredBadge) {
    t6 = () => {
      if (!showCommunityPoweredBadge || !firstInstructorImageUrl) {
        return null;
      }
      return <TrackImpression trackFunc={handleBadgeImpression} testId="instructor-community-badge-impression"><div className={styles["instructor-header-leading"]} data-purpose="instructor-header-image"><div className={styles["instructor-header-image-container"]}><CommunityPoweredBadge imageUrl={firstInstructorImageUrl} displayName={firstInstructor.name} url={firstInstructor.url} onClick={handleBadgeSelected} size="sm" /></div></div></TrackImpression>;
    };
    $[13] = firstInstructor;
    $[14] = firstInstructorImageUrl;
    $[15] = handleBadgeImpression;
    $[16] = handleBadgeSelected;
    $[17] = showCommunityPoweredBadge;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const renderInstructorHeaderImage = t6;
  let t7;
  if ($[19] !== gettext || $[20] !== handleBadgeSelected || $[21] !== instructors || $[22] !== interpolate || $[23] !== isAGwGCourse || $[24] !== showCommunityPoweredBadge) {
    t7 = () => {
      const Names = () => {
        if (isAGwGCourse) {
          return <Button componentClass="a" udStyle="link" className={classNames("ud-text-sm", styles["instructor-link"])} onClick={handleOnInstructorNameClick} href={generateInstructorAnchorHash(0)}>Google</Button>;
        }
        const instructorButtons = instructors.map((instructor_0, index) => <Button componentClass="a" udStyle="link" key={instructor_0.id} className={classNames("ud-text-sm", styles["instructor-link"])} href={instructor_0.url} onClick={index === 0 && showCommunityPoweredBadge ? handleBadgeSelected : undefined} target="_blank" rel="noopener noreferrer">{instructor_0.name}</Button>);
        return instructorButtons.reduce(_temp2, []);
      };
      return <span className={styles["instructor-container"]}>{isAGwGCourse && <Icon glyph="google" size="xsmall" label={false} className={styles["google-icon"]} />}<LocalizedHtml html={interpolate(gettext("<span class=\"created-by\">Created by</span>"), true)} interpolate={{
          "created-by": <span className={styles["created-by"]} />
        }} /><Names /></span>;
    };
    $[19] = gettext;
    $[20] = handleBadgeSelected;
    $[21] = instructors;
    $[22] = interpolate;
    $[23] = isAGwGCourse;
    $[24] = showCommunityPoweredBadge;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const renderInstructorNames = t7;
  if (!showCommunityPoweredBadge) {
    let t8;
    if ($[26] !== renderInstructorNames) {
      t8 = renderInstructorNames();
      $[26] = renderInstructorNames;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  let t8;
  if ($[28] !== renderInstructorHeaderImage) {
    t8 = renderInstructorHeaderImage();
    $[28] = renderInstructorHeaderImage;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== renderInstructorNames) {
    t9 = renderInstructorNames();
    $[30] = renderInstructorNames;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CommunityPoweredRibbon />;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t9) {
    t11 = <div className={styles["instructor-header-details"]}>{t9}{t10}</div>;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11 || $[36] !== t8) {
    t12 = <div className={styles["with-community-badge"]} data-purpose="instructor-name-top">{t8}{t11}</div>;
    $[35] = t11;
    $[36] = t8;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
}
function _temp2(acc, curr, index_0) {
  if (index_0 === 0) {
    return [curr];
  }
  return [...acc, ", ", curr];
}
function _temp(event) {
  const target = event.target as HTMLElement;
  const parentElement = target.parentElement as HTMLAnchorElement | null;
  const position = parentElement?.dataset?.position || "1";
  const id = generateInstructorAnchorHash(parseInt(position, 10) - 1);
  if (parentElement) {
    parentElement.href = `${window.location.search}${id}`;
  }
}